import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Typography,
  Box,
  Paper,
  styled,
  Tooltip
} from "@mui/material";
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';

// Icons
import SummarizeIcon from "@mui/icons-material/Summarize";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import InfoIcon from "@mui/icons-material/Info";
import NotificationsIcon from "@mui/icons-material/Notifications";

// Define information tabs
const infoTabs = [
  { 
    tab: "summary", 
    label: "Meldeübersicht", 
    icon: <SummarizeIcon />,
    description: "Übersicht über gemeldete Teilnehmer" 
  },
  { 
    tab: "cards", 
    label: "Wettkampfkarten", 
    icon: <DescriptionIcon />,
    description: "Abgabe und Verwaltung der Wettkampfkarten" 
  },
  { 
    tab: "schedule", 
    label: "Startzeiten", 
    icon: <EventIcon />,
    description: "Startzeiten für gemeldete Athleten" 
  },
  /*{ 
    tab: "info", 
    label: "Informationen", 
    icon: <InfoIcon />,
    description: "Sonstige Informationen zum Wettkampf" 
  }*/
];

// Styled connector between tabs
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #42a5f5 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #42a5f5 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// Styled step icon
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean; hasNotice?: boolean; };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #1976d2 0%, #42a5f5 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #1976d2 0%, #42a5f5 100%)',
  }),
  ...(ownerState.hasNotice && {
    backgroundImage: 'linear-gradient(136deg, #f57c00 0%, #ffb74d 100%)',
  }),
}));

// Notification indicator
const NoticeIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -5,
  right: -5,
  width: 20,
  height: 20,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
}));

// Step icon component with notice indicator
function StepIconWithNotice(props: StepIconProps & { 
  notice?: ValidationItem; 
  stepIcon: React.ReactNode 
}) {
  const { active, completed, className, notice, stepIcon, ...otherProps } = props;
  
  const hasNotice = notice?.status === true;
  const noticeText = hasNotice ? notice.message : '';

  return (
    <Tooltip title={noticeText} arrow placement="top">
      <div>
        <ColorlibStepIconRoot ownerState={{ completed, active, hasNotice }} className={className}>
          {stepIcon}
          {hasNotice && (
            <NoticeIndicator>
              <NotificationsIcon color="warning" sx={{ fontSize: 16 }} />
            </NoticeIndicator>
          )}
        </ColorlibStepIconRoot>
      </div>
    </Tooltip>
  );
}

interface ValidationItem {
  status: boolean;
  message: string;
}

interface CompetitionInfoTabsProps {
  notices?: Record<string, ValidationItem>;
}

const CompetitionInfoTabs: React.FC<CompetitionInfoTabsProps> = ({ notices = {} }) => {
  const { competitionId } = useParams(); 
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Determine active tab based on current URL
  useEffect(() => {
    const currentPath = location.pathname;
    const tabIndex = infoTabs.findIndex(tab => 
      currentPath.includes(`/${tab.tab}`)
    );
    
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
    } else {
      // Default to first tab if no match
      setActiveTab(0);
    }
  }, [location.pathname]);

  // Handle tab clicks
  const handleTabClick = (tab: string) => {
    navigate(`/registered-competition/${competitionId}/${tab}`);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Stepper 
        alternativeLabel 
        activeStep={activeTab}
        connector={<ColorlibConnector />}
        nonLinear
      >
        {infoTabs.map((tab, index) => {
          const tabNotice = notices[tab.tab];
          const hasNotice = tabNotice?.status === true;

          return (
            <Step key={tab.tab} completed={false}>
              <StepButton 
                onClick={() => handleTabClick(tab.tab)}
              >
                <StepLabel 
                  StepIconComponent={(props) => 
                    <StepIconWithNotice 
                      {...props}
                      notice={tabNotice}
                      stepIcon={tab.icon}
                    />
                  }
                >
                  <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
                    {tab.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                    {tab.description}
                  </Typography>
                  
                  {/* Notice message display */}
                  {hasNotice && (
                    <Typography 
                      variant="caption" 
                      color="warning.main"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {tabNotice.message}
                    </Typography>
                  )}
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
};

export default CompetitionInfoTabs;